/**
 * PATH: packages/email-hub/src/feature/lib/writebackContract.ts
 * FILE_ID: EMAILHUB-FEATURE-WRITEBACK-CONTRACT-001
 * PURPOSE: FE twin of the frozen provider writeback/restore receipt.
 * WHY: Processed and Not Done actions need stable idempotency, audit and
 *      compensation fields before a restore consumer is implemented.
 */

export type ProviderWritebackOperationV1 = 'process' | 'restore'
export type ProviderWritebackResultV1 =
  | 'requested'
  | 'running'
  | 'completed'
  | 'restored'
  | 'skipped'
  | 'failed'

export interface ProviderWritebackStateV1 {
  schema_version: 'email-provider-writeback.v1'
  operation_id: string
  email_id: string
  organisation_id: number
  mailbox_account_id: number
  provider: 'gmail' | 'outlook'
  operation: ProviderWritebackOperationV1
  policy_version: string
  idempotency_key: string
  result: ProviderWritebackResultV1
  provider_target: string
  pre_state: Record<string, unknown>
  target_state: Record<string, unknown>
  provider_ids: Record<string, unknown>
  result_metadata: Record<string, unknown>
  compensating_action:
    | 'gmail_add_inbox_remove_processed_label'
    | 'outlook_move_to_source_folder'
  audit_id: string
  correlation_id?: string
  error_code?: string
  error_class?: string
  error_detail?: string
  retryable: boolean
  retry_after_seconds?: number | null
  attempt_count: number
  occurred_at: string
}

export function writebackCanRetry(state: ProviderWritebackStateV1): boolean {
  return state.result === 'failed'
}

export function writebackIsTerminal(state: ProviderWritebackStateV1): boolean {
  return ['completed', 'restored', 'skipped'].includes(state.result)
}

/**
 * Single source of truth for the operator-facing writeback status copy.
 * Change #269: `buildEmailNotices` reuses these verbatim for the `info`
 * Notice Center entry (non-failure results) so the collapsed notice line and
 * the expanded ProviderWritebackControls band never drift into two labels
 * for the same state.
 */
export const PROVIDER_WRITEBACK_LABELS: Record<ProviderWritebackResultV1, string> = {
  requested: 'Provider update queued',
  running: 'Updating provider',
  completed: 'Processed in mailbox',
  failed: 'Provider update failed',
  restored: 'Restored in mailbox',
  skipped: 'Provider update skipped',
}

